<script setup lang="ts">
import type { BlockStepsStep } from '#shared/types/schema';
import Text from '~/components/base/Text.vue';

interface BlockStepsProps {
	data: {
		id?: string;
		tagline?: string | null;
		headline?: string | null;
		block_steps?: BlockStepsStep[] | string[];
	};
}

const props = defineProps<BlockStepsProps>();

const { setAttr } = useVisualEditing();

// Filtra e ordina gli step validi
const validSteps = computed(() => {
	if (!props.data.block_steps) return [];
	
	return props.data.block_steps
		.filter((step): step is BlockStepsStep => {
			return typeof step === 'object' && step !== null && 'id' in step;
		})
		.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
});

// Funzione per formattare il numero dello step (STEP 01, STEP 02, ecc.)
const formatStepNumber = (index: number) => {
	return `STEP ${String(index + 1).padStart(2, '0')}`;
};
</script>

<template>
	<section>
		<div class="text-left md:text-center">
			<Tagline
				v-if="data.tagline"
				:tagline="data.tagline"
				:data-directus="
					data.id
						? setAttr({
								collection: 'block_steps',
								item: data.id,
								fields: 'tagline',
								mode: 'popover',
							})
						: undefined
				"
			/>
			<Headline
				v-if="data.headline"
				:headline="data.headline"
				text-color="text-black"
				:data-directus="
					data.id
						? setAttr({
								collection: 'block_steps',
								item: data.id,
								fields: 'headline',
								mode: 'popover',
							})
						: undefined
				"
			/>
		</div>

		<div
			v-if="validSteps.length > 0"
			class="flex flex-col md:flex-row gap-8 md:gap-12 md:justify-center items-start mt-8"
			:data-directus="
				data.id
					? setAttr({
							collection: 'block_steps',
							item: data.id,
							fields: ['block_steps'],
							mode: 'modal',
						})
					: undefined
			"
		>
			<div
				v-for="(step, index) in validSteps"
				:key="step.id"
				class="flex flex-col items-start text-left w-full md:w-auto"
			>
				<Tagline
					:tagline="step.tagline || formatStepNumber(index)"
					:data-directus="
						data.id && step.id
							? setAttr({
									collection: 'block_steps_steps',
									item: step.id,
									fields: 'tagline',
									mode: 'popover',
								})
							: undefined
					"
				/>
				<h3
					v-if="step.headline"
					class="font-heading text-black font-normal text-lg md:text-xl lg:text-tagline"
					:data-directus="
						data.id && step.id
							? setAttr({
									collection: 'block_steps_steps',
									item: step.id,
									fields: 'headline',
									mode: 'popover',
								})
							: undefined
					"
				>
					{{ step.headline }}
				</h3>
				<Text
					v-if="step.content"
					:content="step.content"
					text-color="text-black"
					class="mt-4 w-full md:max-w-xs text-sm prose-sm [&_p]:!mb-0 [&_p+_p]:!mt-0 prose-p:text-black prose-p:text-left prose-p:text-sm prose-headings:text-black prose-headings:text-left prose-ul:text-black prose-ul:text-left prose-ol:text-black prose-ol:text-left prose-li:text-black prose-li:text-left prose-blockquote:text-black prose-blockquote:text-left"
					:data-directus="
						data.id && step.id
							? setAttr({
									collection: 'block_steps_steps',
									item: step.id,
									fields: 'content',
									mode: 'drawer',
								})
							: undefined
					"
				/>
			</div>
		</div>
	</section>
</template>

